import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Controller } from "react-hook-form";

import {
  type DragEndEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type {
  QuestionnaireFormValues,
  QuestionnaireQuestionFormValues,
} from "../types";
import { useQuestionnaireFormData } from "./useQuestionnaireFormData";
import { questionnaireBuilderService } from "../services/QuestionnaireBuilderService";

export type { QuestionnaireFormValues, QuestionnaireQuestionFormValues };

const DEFAULT_ORGANIZATION_ID = "12345678901234";
const EMPTY_TEXT_VALUE = "";

type ComparableQuestionSnapshot = {
  id: string;
  label: string;
  type: QuestionnaireQuestionFormValues["type"];
  isRequired: boolean;
  helperText: string;
  optionsCsv: string;
  allowMultipleAnswers: boolean;
  isPersistent: boolean;
};

type ComparableFormSnapshot = {
  title: string;
  description: string;
  usageType: QuestionnaireFormValues["usageType"];
  questions: ComparableQuestionSnapshot[];
};

function toComparableSnapshot(
  values: QuestionnaireFormValues,
): ComparableFormSnapshot {
  return {
    title: values.title ?? EMPTY_TEXT_VALUE,
    description: values.description ?? EMPTY_TEXT_VALUE,
    usageType: values.usageType,
    questions: values.questions.map((question) => ({
      id: question.id,
      label: question.label ?? EMPTY_TEXT_VALUE,
      type: question.type,
      isRequired: question.isRequired,
      helperText: question.helperText ?? EMPTY_TEXT_VALUE,
      optionsCsv: question.optionsCsv ?? EMPTY_TEXT_VALUE,
      allowMultipleAnswers: Boolean(question.allowMultipleAnswers),
      isPersistent: question.isPersistent !== false,
    })),
  };
}

function toSnapshotHash(values: QuestionnaireFormValues): string {
  return JSON.stringify(toComparableSnapshot(values));
}

export interface UseQuestionnaireBuilderParams {
  questionnaireId?: string;
  organizationId?: string;
  /**
   * Quando true, salvamentos intermediários (adição de pergunta e botão de salvar)
   * usam `Questionnaire.status = unknown` no FHIR (fluxo aberto a partir de Gerenciar).
   */
  isManageEditFlow?: boolean;
  /** Chamado ao fechar o NextStepsDialog (ex: navegação para gerenciar questionários). */
  onCloseNextStepsDialog?: () => void;
}

export interface UseQuestionnaireBuilderResult {
  // estado de página
  activeQuestionIndex: number;
  activeQuestion?: QuestionnaireQuestionFormValues;
  isConfigCollapsed: boolean;
  isNextStepsDialogOpen: boolean;
  isQuestionnaireEmpty: boolean;
  isLoadingQuestionnaire: boolean;

  // react-hook-form
  control: ReturnType<typeof useQuestionnaireFormData>["control"];
  controller: typeof Controller;
  handleSubmitQuestionnaire: () => void;
  formIsDirty: boolean;

  // submit
  isSubmitting: boolean;
  hasSubmitError: boolean;

  // draft (config section)
  isDraftSubmitting: boolean;
  hasDraftSaveError: boolean;
  isAddingQuestion: boolean;
  isTitleEmpty: boolean;
  hasUnsavedChanges: boolean;

  // campos dinâmicos
  questionFields: ReturnType<typeof useQuestionnaireFormData>["questionFields"];
  watchedValues: QuestionnaireFormValues;

  // drag and drop
  sensors: ReturnType<typeof useSensors>;
  dndCollisionDetection: typeof closestCenter;
  dndSortingStrategy: typeof verticalListSortingStrategy;
  handleDragEnd: (event: DragEndEvent) => void;

  // handlers de interação
  handleAddNewQuestion: () => void;
  handleDeleteQuestion: (index: number) => void;
  handleDuplicateQuestion: (index: number) => void;
  handleChangeActiveQuestion: (nextIndex: number) => void;
  handleToggleConfigCollapsed: () => void;
  handleCollapseConfig: () => void;
  handleSaveConfigSection: () => void;
  handleCloseNextStepsDialog: () => void;

  /** Rótulo do botão de persistência intermediária (rascunho vs edição). */
  intermediateSaveButtonLabel: string;
  isManageEditFlow: boolean;
  /** Título do herói da página (novo vs editar vs carregando). */
  pageHeroTitle: string;
}

export function useQuestionnaireBuilder(
  params: UseQuestionnaireBuilderParams = {},
): UseQuestionnaireBuilderResult {
  const questionnaireId = params.questionnaireId?.trim() || undefined;
  const organizationId = params.organizationId?.trim() || DEFAULT_ORGANIZATION_ID;
  const isManageEditFlow = Boolean(params.isManageEditFlow);

  const intermediateSaveButtonLabel = useMemo(
    () => (isManageEditFlow ? "Salvar edição" : "Salvar rascunho"),
    [isManageEditFlow],
  );

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [isConfigCollapsed, setIsConfigCollapsed] = useState(false);
  const [isNextStepsDialogOpen, setIsNextStepsDialogOpen] = useState(false);
  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);

  const pageHeroTitle = useMemo(() => {
    if (isLoadingQuestionnaire) return "Carregando...";
    if (questionnaireId) return "Editar questionário";
    return "Novo questionário";
  }, [isLoadingQuestionnaire, questionnaireId]);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    setFocus,
    trigger,
    formIsDirty,
    questionFields,
    watchedValues,
    append,
    move,
    update,
    remove,
    insert,
  } = useQuestionnaireFormData();
  const [lastPersistedSnapshotHash, setLastPersistedSnapshotHash] = useState(
    () => toSnapshotHash(getValues()),
  );

  useEffect(() => {
    if (!questionnaireId) return;

    let cancelled = false;
    setIsLoadingQuestionnaire(true);

    questionnaireBuilderService
      .getById(questionnaireId)
      .then((payload) => {
        if (cancelled || !payload) return;
        const formValues: QuestionnaireFormValues = {
          id: payload.id,
          title: payload.title,
          description: payload.description,
          usageType: payload.usageType,
          questions:
            payload.questions.length > 0
              ? payload.questions.map((q) => ({
                  ...q,
                  allowMultipleAnswers: q.allowMultipleAnswers ?? false,
                  isPersistent: q.isPersistent ?? true,
                }))
              : [
                  {
                    id: "q-1",
                    label: "",
                    type: "shortText" as const,
                    isRequired: false,
                    helperText: "",
                    optionsCsv: "",
                  allowMultipleAnswers: false,
                    isPersistent: true,
                  },
                ],
        };
        reset(formValues);
        setLastPersistedSnapshotHash(toSnapshotHash(formValues));
        setActiveQuestionIndex(0);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingQuestionnaire(false);
      });

    return () => {
      cancelled = true;
    };
  }, [questionnaireId, reset]);

  useEffect(() => {
    const maxIndex = Math.max(0, questionFields.length - 1);
    setActiveQuestionIndex((prev) => Math.max(0, Math.min(prev, maxIndex)));
  }, [questionFields.length]);

  const activeQuestion = watchedValues.questions[activeQuestionIndex];

  const currentSnapshotHash = useMemo(
    () => toSnapshotHash(watchedValues),
    [watchedValues],
  );
  const hasUnsavedChanges = currentSnapshotHash !== lastPersistedSnapshotHash;
  const isTitleEmpty =
    (watchedValues.title?.trim() ?? "").length === 0;

  const saveQuestionnaireTask = useCallback(
    async (values: QuestionnaireFormValues): Promise<void> => {
      const result = await questionnaireBuilderService.save(values, organizationId);
      if (result.id) {
        setValue("id", result.id, { shouldDirty: false });
      }
      toast.success("Questionário publicado.");
      setIsNextStepsDialogOpen(true);
    },
    [setValue, organizationId],
  );

  const {
    run,
    loading: isSubmitting,
    error,
  } = useAsyncTask<[QuestionnaireFormValues], void>({
    task: saveQuestionnaireTask,
  });

  type SaveDraftOptions = {
    collapseAfterSave?: boolean;
    showSuccessToast?: boolean;
  };

  const persistIntermediateTask = useCallback(
    async (values: QuestionnaireFormValues, options?: SaveDraftOptions) => {
      const persist = isManageEditFlow
        ? questionnaireBuilderService.saveEditingUnknown
        : questionnaireBuilderService.saveDraft;

      const result = await persist(values, organizationId);
      if (result.id) {
        setValue("id", result.id, { shouldDirty: false });
      }
      setLastPersistedSnapshotHash(toSnapshotHash(values));
      if (options?.showSuccessToast !== false) {
        toast.success(
          isManageEditFlow ? "Edição salva." : "Rascunho salvo.",
        );
      }
      if (options?.collapseAfterSave !== false) {
        setIsConfigCollapsed(true);
      }
      return result;
    },
    [isManageEditFlow, organizationId, setValue],
  );

  const {
    run: runSaveDraft,
    loading: isDraftSubmitting,
    error: draftError,
  } = useAsyncTask<
    [QuestionnaireFormValues, SaveDraftOptions?],
    Awaited<ReturnType<typeof persistIntermediateTask>>
  >({
    task: persistIntermediateTask,
  });

  const handleChangeActiveQuestion = useCallback(
    (nextIndex: number) => {
      const isIntermediateSaveEnabled =
        !isDraftSubmitting && !isTitleEmpty && hasUnsavedChanges;

      if (isIntermediateSaveEnabled) {
        void runSaveDraft(getValues(), {
          collapseAfterSave: false,
          showSuccessToast: false,
        });
      }

      setActiveQuestionIndex(nextIndex);
    },
    [
      getValues,
      hasUnsavedChanges,
      isDraftSubmitting,
      isTitleEmpty,
      runSaveDraft,
    ],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = questionFields.findIndex(
        (questionField) => questionField.id === active.id,
      );
      const newIndex = questionFields.findIndex(
        (questionField) => questionField.id === over.id,
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      move(oldIndex, newIndex);
      setActiveQuestionIndex(newIndex);
    },
    [move, questionFields],
  );

  const handleDeleteQuestion = useCallback(
    (index: number) => {
      if (questionFields.length <= 1) {
        return;
      }
      remove(index);
      const nextIndex = (() => {
        if (activeQuestionIndex === index) {
          return Math.max(0, index - 1);
        }
        if (activeQuestionIndex > index) {
          return activeQuestionIndex - 1;
        }
        return activeQuestionIndex;
      })();
      setActiveQuestionIndex(
        Math.max(0, Math.min(nextIndex, questionFields.length - 2)),
      );
    },
    [questionFields.length, activeQuestionIndex, remove],
  );

  const handleDuplicateQuestion = useCallback(
    (index: number) => {
      const source = watchedValues.questions[index];
      if (!source) return;
      const cloned = {
        ...source,
        id: `q-${Date.now()}`,
        isPersistent: true,
      };
      insert(index + 1, cloned);
      setActiveQuestionIndex(
        Math.max(0, Math.min(index + 1, questionFields.length)),
      );
    },
    [insert, watchedValues.questions, questionFields.length],
  );

  const handleAddNewQuestion = useCallback(async () => {
    if (isAddingQuestion) return;

    setIsAddingQuestion(true);

    try {
      const newQuestionId = `q-${Date.now()}`;
      const newQuestionIndex = questionFields.length;

      append({
        id: newQuestionId,
        label: "",
        type: "shortText",
        isRequired: false,
        helperText: "",
        optionsCsv: "",
        allowMultipleAnswers: false,
        isPersistent: true,
      });

      setActiveQuestionIndex(newQuestionIndex);
      const valuesAfterAppend = getValues();
      if ((valuesAfterAppend.title?.trim() ?? "").length > 0) {
        await runSaveDraft(valuesAfterAppend, { collapseAfterSave: false });
      }
    } finally {
      setIsAddingQuestion(false);
    }
  }, [
    append,
    questionFields.length,
    getValues,
    runSaveDraft,
    isAddingQuestion,
  ]);

  const handleToggleConfigCollapsed = useCallback(() => {
    setIsConfigCollapsed((prev) => !prev);
  }, []);

  const handleCollapseConfig = useCallback(() => {
    setIsConfigCollapsed(true);
  }, []);

  const isQuestionnaireEmpty = questionFields.length === 0;

  const handleSaveConfigSection = useCallback(() => {
    if (!isTitleEmpty) {
      void runSaveDraft(getValues());
    }
  }, [runSaveDraft, getValues, isTitleEmpty]);

  const handleCloseNextStepsDialog = useCallback(() => {
    setIsNextStepsDialogOpen(false);
    params.onCloseNextStepsDialog?.();
  }, [params.onCloseNextStepsDialog]);

  const expandConfigAndFocusTitle = useCallback(() => {
    setIsConfigCollapsed(false);
    setTimeout(() => {
      trigger("title");
      setFocus("title");
    }, 150);
  }, [trigger, setFocus]);

  const handleSubmitQuestionnaire = handleSubmit(
    (values) => {
      if ((values.title?.trim() ?? "").length === 0) {
        expandConfigAndFocusTitle();
        return;
      }
      run(values);
    },
    expandConfigAndFocusTitle,
  );

  return {
    activeQuestionIndex,
    activeQuestion,
    isLoadingQuestionnaire,
    isConfigCollapsed,
    isNextStepsDialogOpen,
    isQuestionnaireEmpty,
    control,
    controller: Controller,
    handleSubmitQuestionnaire,
    formIsDirty,
    isSubmitting,
    hasSubmitError: Boolean(error),
    isDraftSubmitting,
    hasDraftSaveError: Boolean(draftError),
    isAddingQuestion,
    isTitleEmpty,
    hasUnsavedChanges,
    questionFields,
    watchedValues,
    sensors,
    dndCollisionDetection: closestCenter,
    dndSortingStrategy: verticalListSortingStrategy,
    handleDragEnd,
    handleAddNewQuestion,
    handleDeleteQuestion,
    handleDuplicateQuestion,
    handleChangeActiveQuestion,
    handleToggleConfigCollapsed,
    handleCollapseConfig,
    handleSaveConfigSection,
    handleCloseNextStepsDialog,
    intermediateSaveButtonLabel,
    isManageEditFlow,
    pageHeroTitle,
  };
}
